import React, {
	Fragment,
	useState,
	useEffect,
	useRef,
	useContext,
} from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import debounce from 'lodash/debounce';
import {
	Button,
	IconButton,
	Checkbox,
	Typography,
	OutlinedInput,
	checkboxClasses,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import content from './content';
import { Loader, Modal } from '../../../common';
import { addBulkItemsStyles, addItemsStyles } from './styles';
import { colors } from '../../../theme/color-palette';
import { UserPortalContext } from '../../../context/appcontext';
import {
	getBulkItemSearchResults,
	updateBulkItemSearchResults,
	updateCompareSelectedItemA,
	updateCompareSelectedItemB,
	updateSelectedItem,
} from '../../../redux/actions/createRuleActions';
import { ResultTable } from './SearchResult';
import { Alert } from '../Alerts';

const AddBulkItem = ({
	type,
	isOpen,
	getBulkData,
	isComparison,
	setIsAddBulkItemOpen,
}) => {
	const dispatch = useDispatch();
	const c = content[type]?.addBulk;
	const [inputQuery, setInputQuery] = useState('');
	const [error, setError] = useState(false);
	const [selectAll, setSelectAll] = useState(false);
	const portalUserContext = useContext(UserPortalContext);
	/**
	 * This is used to enable/disable the API hit, to prevent unnecessary calls
	 */
	const [searchEnabled, setSearchEnabled] = useState(true);

	const TYPE = type.toLocaleLowerCase();
	const {
		bulkSearchItems,
		selectedItems,
		selectedItemsA,
		selectedItemsB,
	} = useSelector((state) => state.createRuleReducer);
	const selectedItemsArray = selectedItems[TYPE] || [];
	const selectedItemsArrayA = selectedItemsA[TYPE] || [];
	const selectedItemsArrayB = selectedItemsB[TYPE] || [];

	const searchItems = bulkSearchItems[TYPE] || [];
	const showResults = !bulkSearchItems?.loading && searchItems?.length > 0;
	const [disableDoneButton, setDisableDoneButton] = useState(
		searchItems?.some(({ isValid }) => !isValid) || searchItems.length === 0
	);

	/**
	 * @param {*} itemNum
	 * Remove Item from Bulk Item results
	 * Remove the item from Comma separated Search Inputs
	 */
	const onRemoveItemClick = (itemNum) => {
		const items = [];
		searchItems.forEach((element) => {
			if (element[c['key']] !== itemNum) {
				items.push(element);
			}
		});
		dispatch(updateBulkItemSearchResults({ [TYPE]: items }));

		// Disable API Call
		if (searchEnabled) {
			setSearchEnabled(false);
		}

		const newInputQuery = inputQuery
			?.split(',')
			.filter((e) => e !== itemNum)
			.join(',');
		validateAndSetItemNumbers(newInputQuery);
	};

	useEffect(() => {
		document.addEventListener('keydown', keyDownHandler);
		return () => {
			document.removeEventListener('keydown', keyDownHandler);
		};
	}, []);

	/**
	 * Validate the input User entered
	 * Set to state variable
	 */
	const validateAndSetItemNumbers = (input = '') => {
		// Format user typed input into array of Store(or Item etc) numbers
		const inputNumArray = input
			?.split(/['\n','\s']+/)
			?.map((val) => val?.replace(/\D/g, ''))
			.filter((val) => val);

		let inputQuery = [...new Set(inputNumArray)].join(',');

		if (input.endsWith(',')) {
			inputQuery = `${inputQuery},`;
		}
		setInputQuery(inputQuery);
	};

	const onInputChange = (e) => {
		if (!searchEnabled) {
			setSearchEnabled(true);
		}
		validateAndSetItemNumbers(e.target?.value);
	};

	const clearInput = () => {
		if (!searchEnabled) {
			setSearchEnabled(true);
		}
		setInputQuery('');
		dispatch(updateBulkItemSearchResults([]));
	};

	const keyDownHandler = (e) => {
		/* ctrl+v & cmd+v */
		if ((e.keyCode === 86 && e.ctrlKey) || (e.keyCode === 86 && e.metaKey)) {
			onClipBoardPaste(e);
		}
	};

	const onClipBoardPaste = (e) => {
		e.preventDefault();
		navigator?.clipboard
			?.readText()
			.then(validateAndSetItemNumbers)
			.catch(() => {});
	};

	const debouncedSearch = useRef(
		debounce((params) => {
			if (params?.inputQuery?.length) {
				const inputQueryArr = params?.inputQuery?.split(',');
				if (inputQueryArr?.length > 50) {
					setError(true);
				} else {
					setError(false);
					dispatch(getBulkItemSearchResults(params));
				}
			}
		}, 500)
	).current;

	useEffect(() => {
		if (searchEnabled) {
			debouncedSearch({ type, inputQuery, portalUserContext });
		}
	}, [type, inputQuery, searchEnabled]);

	const getSelectedItems = () => {
		if (isComparison === 'A') {
			return selectedItemsArrayA;
		} else if (isComparison === 'B') {
			return selectedItemsArrayB;
		} else {
			return selectedItemsArray;
		}
	};

	const checkBoxHandler = (e) => {
		setSelectAll(e.target.checked);
		const items = getSelectedItems();
		if (e.target.checked) {
			const finalSelectedItems = searchItems?.map((item) => {
				return {
					...item,
					checked: item?.isValid ? true : false,
				};
			});

			const validateFinal = finalSelectedItems.concat(items);
			setDisableDoneButton(false);

			if (isComparison === 'A') {
				dispatch(updateCompareSelectedItemA({ [TYPE]: validateFinal }));
			} else if (isComparison === 'B') {
				dispatch(updateCompareSelectedItemB({ [TYPE]: validateFinal }));
			} else {
				dispatch(updateSelectedItem({ [TYPE]: validateFinal }));
			}
		} else {
			if (isComparison === 'A') {
				dispatch(updateCompareSelectedItemA({ [TYPE]: [] }));
			} else if (isComparison === 'B') {
				dispatch(updateCompareSelectedItemB({ [TYPE]: [] }));
			} else {
				dispatch(updateSelectedItem({ [TYPE]: [] }));
			}
			setDisableDoneButton(true);
		}
	};

	const parseSearchData = () => {
		if (selectAll) {
			return searchItems?.map((item) => {
				return {
					...item,
					checked: item?.isValid ? true : false,
				};
			});
		} else {
			return searchItems;
		}
	};

	const backButtonHandler = () => {
		setIsAddBulkItemOpen(false);
		dispatch(updateBulkItemSearchResults({ [TYPE]: [] }));
	};

	const onDoneButtonClick = () => {
		const items = searchItems?.filter((item) => item.isValid);
		getBulkData(items);
		onClose();
		dispatch(updateBulkItemSearchResults({ [TYPE]: [] }));
	};

	const onCancelClickHandler = () => {
		onClose();
		dispatch(updateBulkItemSearchResults({ [TYPE]: [] }));
	};

	const onClose = () => setIsAddBulkItemOpen(false);

	const showAlert =
		searchItems?.length > 0 && searchItems.some((item) => !item.isValid);

	return (
		<Modal
			title={
				<Box sx={addBulkItemsStyles.header}>
					<IconButton onClick={backButtonHandler}>
						<ArrowBackIcon style={addBulkItemsStyles.backButton} />
					</IconButton>
					<Typography variant="h2" style={addBulkItemsStyles.title}>
						{c['title']}
					</Typography>
				</Box>
			}
			open={isOpen}
			handleClose={onClose}
			showCloseIcon={false}
			actionButtons={
				<Fragment>
					<Button
						onClick={onCancelClickHandler}
						disableRipple
						style={addBulkItemsStyles.cancelButton}>
						Cancel
					</Button>
					<Button
						onClick={onDoneButtonClick}
						disabled={disableDoneButton}
						style={{
							...addBulkItemsStyles.doneButton,
							backgroundColor: disableDoneButton ? colors.GREY : colors.PURPLE,
						}}>
						Done
					</Button>
				</Fragment>
			}>
			<Box style={{ display: 'flex' }}>
				{/* Search Box */}
				<OutlinedInput
					id="store-no"
					autoComplete="off"
					placeholder={c['inputPlaceholder']}
					sx={{
						...addBulkItemsStyles.searchBox,
						width: inputQuery?.length > 0 ? '618px' : '704px',
					}}
					inputProps={addBulkItemsStyles.searchBoxInput}
					value={inputQuery}
					onChange={onInputChange}
				/>
				{inputQuery?.length > 0 && (
					<Button
						disableRipple
						disabled={bulkSearchItems?.loading}
						variant="text"
						onClick={clearInput}
						style={addBulkItemsStyles.pasteItemLink}>
						{c['clearButton']}
					</Button>
				)}
			</Box>
			{error ? <Typography>Max 50 {type} are allowed</Typography> : null}
			{/* invalid Item Alert  */}
			<Alert
				open={showAlert}
				style={{ marginTop: '24px' }}
				content={`The highlighted ${type}s are invalid.`}
			/>
			{/* Bulk Search Results w/ Loader */}
			<Loader show={bulkSearchItems?.loading} height={'360px'} />
			{showResults && (
				<Fragment>
					<Box style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Typography
							id="modal-modal-title"
							variant="h6"
							component="h2"
							style={addItemsStyles.searchResults}>
							Search Results
						</Typography>
						<Typography style={addItemsStyles.searchResults}>
							{c['selectValid']}{' '}
							<Checkbox
								sx={{
									[`&, &.${checkboxClasses.checked}`]: {
										color: colors.PURPLE,
									},
								}}
								//	checked={row.checked}
								inputProps={{ 'aria-label': 'checkbox' }}
								onChange={(e) => checkBoxHandler(e)}
							/>
						</Typography>
					</Box>
					<ResultTable
						type={TYPE}
						result={parseSearchData()}
						isBulkUpload={true}
						isComparison={isComparison}
						onRemoveItemClick={onRemoveItemClick}
						setDisableDoneButton={setDisableDoneButton}
					/>
				</Fragment>
			)}
		</Modal>
	);
};

AddBulkItem.propTypes = {
	type: PropTypes.string,
	isOpen: PropTypes.bool,
	getBulkData: PropTypes.func,
	isComparison: PropTypes.string,
	setIsAddBulkItemOpen: PropTypes.func,
};

export default AddBulkItem;
